<template>
  <DefaultLayout>
    <div v-if="getCurrentRTLoading || getCourseLoading" class="text-center mt-6">
      <p class="mb-2.5">Loading RoundTable</p>
      <div class="flex justify-center">
        <pl-spinner />
      </div>
    </div>
    <div v-else class="max-w-screen-3xl px-5 py-3">
      <div class="flex justify-between items-center" >
        <div class="flex items-center space-x-2" >
          <DropdownMenu v-if="getCurrentRT.sequence_id !== null">
            <template v-slot:toggle>
              <button class="bg-indigo-500 rounded-full py-1.5 px-3 flex items-center justify-between space-x-2">
                <img src="/static/roundtable_icons/sequence_icon.svg" class="w-4 h-4" />
                <i class="fa-solid text-xs fa-chevron-down text-indigo-100"></i>
              </button>
            </template>
            <template v-slot:menu-content>
              <ul class="absolute z-50 bg-white border border-solid border-gray-200 max-w-lg rounded-lg mt-2 shadow-sm">
                <li 
                  v-for="(roundtable, index) in getCurrentRT.roundtables_sequence" 
                  :key="index" 
                  class="border-b border-gray-200 py-2 px-3 hover:bg-gray-100 cursor-pointer"
                  :class="[{ 'border-none': index + 1 === getCurrentRT.roundtables_sequence.length },{'bg-gray-100':roundtable.id === $route.params.roundtableID}]"
                  @click="handleClickSequenceRoundTable(roundtable.id,roundtable.type)"
                >
                  <div class="flex justify-start items-center space-x-2">
                    <div class="w-7 h-7">
                      <div
                        class="flex items-center justify-center rounded-full h-7 w-7"
                        :class="[{'bg-blue': roundtable.type === 'written' || roundtable.type === 'roundtable', 'bg-green-light':roundtable.type === 'verbal' || roundtable.type === 'live_discussion'}]"
                      >
                        <img
                          :class="[{ 'w-3 h-3': roundtable.type === 'written' },{'w-3 pt-0.5 h-3': roundtable.type === 'verbal'}]"
                          :src="roundtable.type === 'written' || roundtable.type === 'roundtable' ? '/static/roundtable_icons/written_icon.svg' : '/static/roundtable_icons/verbal_icon.svg'"
                          class="w-3 h-3"
                        />
                      </div>
                    </div>
                    <h6 :title="roundtable.title" class="truncate">
                      {{ roundtable.title }}
                    </h6>
                  </div>
                </li>
              </ul>
            </template>
          </DropdownMenu>
          <span class="text-2xl font-extrabold truncate font-manrope text-blueGray-800" :title="getCurrentRT.title">
            {{ getCurrentRT.title }}
          </span>
          <div class="ml-2" v-if="rt_integration">
            <a :href="assignmentUrl" target="blank">
              <img :src="assignmentIcon" width="20px" height="20px" class="rounded-full" />
            </a>
          </div>
          <div
            v-if="getCurrentRT.sequence_id !== null"
            class="bg-indigo-100 text-xs border border-solid border-indigo-500 text-indigo-500 rounded-full px-2 py-0.5"
          >
            <div class="flex justify-between items-center">
              <span>{{ current_sequence_number + 1 }}</span>
              <span class="ml-0.5 mr-1">of</span>
              <span>{{ getCurrentRT.roundtables_sequence.length }}</span>
            </div>
          </div>
        </div>
        <div v-if="hasTeacherPermissionsInCourse" class="flex items-center space-x-1">
          <div v-if="showQuestionIcon" @click="showFirstRTWelcomeModal" class="cursor-pointer text-lg font-bold mr-2">
            <i class="fa-solid fa-circle-info text-lg text-blueGray-700 hover:text-blueGray-800"></i>
          </div>
          <button @click="isInviteStudentModalOpen = true" class="px-5 py-2 bg-pinky hover:bg-pinky-dark rounded-full text-white font-manrope font-semibold text-sm disabled:opacity-50">
            <span class="truncate">Invite Students</span>
          </button>
          <OnlineRoundableMenu
            @showModalEndRoundtableConfirm="openToggleEndRoundtableConfirm"
            @showCloseDateModal="show_close_date_modal = true"
            @showDuplicateModal="show_duplicate_modal = true"
            @showShareActivityModal="showShareRoundtableModal"
            @rtIntegrationAssignment="rtIntegrationAssignment"
            @showFollowUp="(type) => handleOpenStartFollowUP(type)"
            @addResponse="goToSubmitResponsePage"
            :sequence_roundtables="getCurrentRT.roundtables_sequence"
          />
        </div>
        <div v-else-if="getCurrentRT.enable_annonymous && !hasTeacherPermissionsInCourse" class="flex items-center">
          <div v-if="showQuestionIcon" @click="showFirstRTWelcomeModal" class="cursor-pointer text-lg font-bold mx-4">
            <i class="fa-solid fa-circle-info text-lg text-coolGray-700"></i>
          </div>
          <IdentityReveal class="flex items-center mt-1 md:mt-0" />
        </div>
      </div>
      <div v-if="isRoundtableEnded" class="bg-red-100 table mt-1 border py-1.5 px-4 border-solid border-red-400 text-red-500 rounded-full">
        <div class="flex items-center space-x-2">
          <img src="/static/roundtable_icons/closed_on.svg" class="w-4 h-4" />
          <span class="font-poppins text-xs">
            Closed on {{ roundTableCloseDate }}
          </span>
        </div>
      </div>
      <div v-if="getCurrentRT.end_date !== null && isRoundtableEnded === false" class="bg-gray-100 table mt-1 border py-1.5 px-4 border-solid border-gray-300 text-gray-500 rounded-full">
        <div class="flex items-center space-x-2">
          <img src="/static/roundtable_icons/closes_on.svg" class="w-4 h-4" />
          <span class="font-poppins text-xs">
            Closes on {{ roundTableCloseDate }}
          </span>
        </div>
      </div>
      <div class="flex lg:flex-row md:flex-col flex-col h-full gap-y-1 gap-x-1 pt-3">
        <div id="rigthGrid" :class="gridLeft">
          <div class="bg-white border border-coolGray-200 rounded-lg">
            <div>
              <div class="flex justify-between items-center w-full pt-2.5 pr-2 border-b border-solid border-gray-200">
                <div class="flex items-center rounded-t overflow-x-auto">
                  <div @click="goToTab('prompt')" :class="{'border-primary text-primary border-b': tab === 'prompt'}" style="height: 30px" class="px-5 select-none cursor-pointer">
                    Prompt
                  </div>
                  <div v-if="hasTeacherPermissionsInCourse" @click="goToTab('settings')" :class="{'border-primary text-primary border-b': tab === 'settings'}" style="height: 30px" class="select-none px-5 cursor-pointer">
                    Settings
                  </div>
                  <div @click="goToTab('discussion')" :class="{'border-primary text-primary border-b': tab === 'discussion'}" style="height: 30px" class=" select-none px-5 cursor-pointer">
                    Discussion
                  </div>
                  <div @click="goToTab('summary')" :class="{'border-primary text-primary border-b': tab === 'summary'}" style="height: 30px" class=" px-5 select-none cursor-pointer">
                    Summary
                  </div>
                  <div v-if="hasTeacherPermissionsInCourse" @click="goToTab('individual_summary')" :class="{'border-primary text-primary border-b': tab === 'individual_summary'}" style="height: 30px" class=" px-5 select-none cursor-pointer">
                    Table
                  </div>
                </div>
                <DropdownMenu>
                  <template v-slot:toggle>
                    <button v-if="show_tab_menu === true" class="rounded-full hover:bg-gray-100 mb-2.5 items-center justify-between space-x-2">
                      <i class="fa-solid fa-circle mt-0.5 text-primary" style="font-size: 5px"></i>
                      <span>{{ tab === 'individual_summary' ? 'Table' : tab.charAt(0).toUpperCase() + tab.slice(1)}}</span>
                      <i class="fa-solid text-xs fa-chevron-down text-gray-700"></i>
                    </button>
                  </template>
                  <template v-slot:menu-content>
                    <ul class="absolute z-10 bg-white border border-solid border-gray-200 max-w-lg rounded-lg mt-2 shadow-sm">
                      <li @click="goToTab('prompt')" class="cursor-pointer py-2 px-3 hover:bg-gray-100 border-b border-solid border-gray-200">
                        Prompt
                      </li>
                      <li v-if="hasTeacherPermissionsInCourse" @click="goToTab('settings')" class="cursor-pointer py-2 px-3 hover:bg-gray-100 border-b border-solid border-gray-200">
                        Settings
                      </li>
                      <li @click="goToTab('discussion')" class="cursor-pointer py-2 px-3 hover:bg-gray-100 border-b border-solid border-gray-200">
                        Discussion
                      </li>
                      <li @click="goToTab('summary')" class="cursor-pointer py-2 px-3 hover:bg-gray-100 border-b border-solid border-gray-200">
                        Summary
                      </li>
                      <li v-if="hasTeacherPermissionsInCourse" @click="goToTab('individual_summary')" class="cursor-pointer py-2 px-3 hover:bg-gray-100">
                        Table
                      </li>
                    </ul>
                  </template>
                </DropdownMenu>
                <div v-if="tab === 'prompt'" class="text-gray-800">
                  <div v-if="!hasTeacherPermissionsInCourse" class="flex items-center space-x-2 mr-3 -mt-2">
                    <img :src="author_avatar" class="w-5 h-5 rounded-full"/>
                    <span class="font-manrope text-gray-800">by {{ author_name }}</span>
                  </div>
                  <button v-else class="flex items-center space-x-2 hover:underline -mt-2 mr-3.5 rounded" @click="handleEditPrompt()">
                    <h6>Edit Prompt</h6>
                  </button>
                </div>
                <div v-else-if="tab === 'discussion'">
                  <Tooltip
                    text="Show List"
                    styleTooltip="top-0 right-full -mt-2.5 mr-2"
                  >
                    <toggle-button
                      :sync="true"
                      :labels="false"
                      v-model="toggle_responses"
                      color="#72ccd7"
                      class="toggle-button-class -mt-3"
                    />
                  </Tooltip>
                </div>
                <div v-else-if="tab === 'settings'" class="-mt-2 flex items-center space-x-3 mr-3">
                  <button
                    v-if="is_button_save_setting_enable !== true"
                    @click="forceRerender"
                    class="sample hover:underline text-gray-500 rounded disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    @click="is_settings_confirm_modal_open = true"
                    :class="is_button_save_setting_enable === true ? 'text-gray-400 cursor-not-allowed' : 'text-gray-800'"
                    class="sample hover:underline rounded disabled:opacity-50"
                    :disabled="is_button_save_setting_enable"
                  >
                    <i class="fa-solid fa-floppy-disk mr-1.5"></i>Save
                  </button>
                </div>
              </div>
              <div :class="tab === 'prompt' ? 'prompt-heigth overflow-y-auto' : ''">
                <Component
                  v-if="getCurrentRT.id && isLoadingWritten === false"
                  :is="component_to_render"
                  :key="componentKey"
                  ref="childComponent"
                  @editAssessmentPoint="editAssessmentPoint"
                  @editPeerQuestion="editPeerQuestion"
                  @deletePeerQuestion="deletePeerQuestion"
                  @cancelSettingsChanges="forceRerender"
                  @handleSettingsChange="(value) => (is_button_save_setting_enable = value)"
                  @openAssessmentTab=" open_assessment = true; toggle_responses = true;"
                  @orderedSubmissions="(value) => (orderedSubmissions = value)"
                />
              </div>
            </div>
          </div>
        </div>
        <div v-if="showRightContainer" :class="gridRight">
          <div v-if="tab === 'discussion'" class=" sticky top-1">
            <div v-if="!genie_recap">
              <OnlineStudentsList
                v-if="!open_assessment"
                :inViewport="first_in_viewport"
                @enableSubmission="() => (isLoadingWritten = false)"
                @showInviteModal="showShareRoundtableModal"
                @selectSubmission="selectSubmission"
                @unsubmittedDraftResponse="unsubmittedDraftResponse"
                @draftEvaluations="showDraftEvaluations"
                key="list"
              />
              <OnlineStudentsAssessment
                v-if="open_assessment"
                :orderedSubmissions="orderedSubmissions"
                @backToOnlineStudentList="open_assessment = false"
              />
            </div>
            <GenieRecapSideBar
              v-else
              @backToOnlineStudentList="genie_recap = false"
              @startFollowUp="(type) => handleOpenFollowUpQuestions(type)"
              @editFollowUpQuestions="editFollowUpQuestions"
            />
          </div>
          <div v-if="showSubmissionContainer" class="bg-white border-coolGray-200 border rounded-lg h-full">
            <div class="h-full">
              <div style="height: 41px;" class="px-4 py-0.5 flex justify-between items-center border-b border-solid border-gray-200">
                <div class="flex items-center space-x-2.5">
                  <h5 class="text-sm text-gray-800">
                    <span class="font-semibold">My Submission</span> <span class="font-manrope">({{ wordCount }} words)</span>
                  </h5>
                </div>
                
                <div class="flex items-center">
                  <h6 class="mr-3 text-gray-300">
                    {{ draftResponseMessage }}
                  </h6>
                </div>
              </div>
              <SubmissionForm
                v-if="!isLoadingWritten"
                :submissionData="getMySubmission"
                @updateDiscussion="goToDiscussion"
                @submissionChanged="handleSubmissionChange"
                @changingPrompt="isPromptChanging"
                @wordcount="wordCount = $event"
                @enableEditSubmission="(isEnable) => (isEnableEditSubmission = isEnable)"
                @messageDraftResponse="(message) => (draftResponseMessage = message)"
              />
            </div>
          </div>
        </div>
      </div>
      <div key="dialogs">
        <ShareActivityModal
          :activityType="'written'"
          :preSelectedTab="pre_selected_tab_in_share_modal"
          @close="closeShareModal"
          v-if="show_share_roundtable_modal"
        />
        <CloseRoundTableModal
          v-if="show_end_reopen_modal"
          :title="confirm.title"
          :content="confirm.content"
          @close="show_end_reopen_modal = false"
          @hardClose="(hardClose) => (hard_close = hardClose)"
          @closeRoundTable="handleCloseRoundTable"
        />
        <DuplicateRoundTableModal
          @close="show_duplicate_modal = false"
          v-if="show_duplicate_modal"
        />
        <close-date-modal
          @close="show_close_date_modal = false"
          v-if="show_close_date_modal"
        />
        <integrations-connect-modal
          @close="open_google_classroom_dialog = false"
          v-if="open_google_classroom_dialog"
        />
        <integration-connected-modal
          v-if="is_integration_connected"
          :integration="integration"
          @close="is_integration_connected = false"
        />
        <create-new-point-modal
          :activityType="'written'"
          :tag="selected_point_tag"
          :rts="roundtables_with_point"
          :assessment_scale="selected_point_scale"
          @deleteAssessmentPoint="deleteAssessmentPoint"
          v-if="show_point_creation_modal"
          @close="show_point_creation_modal = false"
        />
        <SyncAssessmentDataModal
          v-if="sync_assessment"
          @close="sync_assessment = false"
          :activityType="activityType"
          :edLink="edLink"
          :selectedIntegration="selected_integration"
          :logo="logo"
          :integrationName="integrationName"
        />
        <template v-if="show_alert_assessment_changing">
          <pl-dialog
            size="confirm_size"
            :title="'Heads up!'"
            :disableOutsideClick="true"
            :showCloseButton="false"
          >
            <div class="px-6 flex flex-col pb-5">
              <div class="flex-grow mb-6">
                <div>
                  <p class="mb-4">
                    Changing (or deleting) this assessment criteria for this
                    RoundTable will change (or delete) it for all of the other
                    RoundTables in this course that also use this criteria:
                  </p>
                </div>
                <ul>
                  <li v-for="roundtable in roundtables_with_point" :key="roundtable.id" class="mb-1">
                    <span class="font-semibold font-poppins">- {{ roundtable.title }}</span>
                  </li>
                </ul>
              </div>
              <div class="flex flex-col space-y-4 w-full">
                <button @click="closeAssessmentChangingAlert" class="bg-primary w-full hover:bg-primary-dark text-white rounded px-3 py-2">
                  Okay, Got it
                </button>
                <div class="flex justify-center items-center space-x-2">
                  <span class="font-poppins text-gray-800">Do not show this anymore</span>
                  <input
                    v-model="not_show_heads_up_info"
                    type="checkbox"
                    class="cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </pl-dialog>
        </template>
        <pl-dialog :disableOutsideClick="true" title="Delete Criteria Point" size="confirm_size" v-if="show_delete_point_modal" @close="closeDeletePointModal">
          <div class="h-full w-full overflow-y-auto">
            <div class="flex flex-col h-full pb-5">
              <div class="overflow-y-auto h-full px-6 mb-4">
                <div class="text-gray-900 font-poppins font-light">
                  You are deleting this criteria point from this roundtable. This action cannot be undone.
                </div>
              </div>
              <div class="flex flex-col px-5 flex-grow h-full">
                <div class="flex flex-col w-full space-y-1.5 mb-2">
                  <span class="font-poppins text-gray-900">Please type in "confirm" to delete</span>
                  <input
                    ref="confirm-input"
                    @keyup.enter="confirmDeleteAssessmentPoint()"
                    placeholder="Type 'confirm' to delete roundTable from the course"
                    type="text"
                    class="py-2 px-3 w-full border border-solid border-gray-200 bg-white rounded-md focus-within:border-red-500"
                  />
                </div>
                <div class="flex items-center space-x-2">
                  <button
                    @click="show_delete_point_modal = false"
                    class="hover:bg-gray-50 bg-white py-2 flex items-center justify-center space-x-2 text-blueGray-500 font-medium w-full rounded-md border border-solid border-gray-200"
                  >
                    <span>Cancel</span>
                  </button>
                  <button
                    @click="confirmDeleteAssessmentPoint"
                    class="bg-red-500 transition-colors py-2 flex items-center justify-center space-x-2 text-red-50 font-semibold w-full rounded-md border border-solid border-red-500"
                  >
                    <span>Confirm</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </pl-dialog>
        <!-- <dialog-delete-confirm
          v-if="show_delete_point_modal"
          @close="closeDeletePointModal"
          @actionConfirmed="confirmDeleteAssessmentPoint"
          optionalText="Are you sure you want to delete this Point?"
        /> -->
        <pl-dialog
          :size="'sm'"
          :title="'Edit Question'"
          :disableOutsideClick="true"
          @close="show_edit_question_modal = false"
          v-if="show_edit_question_modal"
        >
          <div class="px-7 pt-7 pb-4 flex-grow overflow-y-auto">
            <div class="text-xs">Question</div>
            <input
              type="text"
              class="bg-input text-sm text-gray-500 flex-grow outline-none px-4 py-2 w-full rounded"
              v-model="
                peer_assessment_questions[question_to_edit_index].question
              "
            />
          </div>
          <div class="flex items-center justify-center my-5">
            <button
              class="px-8 py-2 hover:bg-gray-100 rounded cursor-pointer text-gray-500 font-semibold mr-2.5"
              @click="show_edit_question_modal = false"
            >
              Cancel
            </button>
            <button
              class="sample px-8 py-2 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50"
              @click="saveQuestion"
            >
              Edit
            </button>
          </div>
        </pl-dialog>
        <DialogDeleteConfirm
          v-if="show_delete_question_modal"
          @close="show_delete_question_modal = false"
          @actionConfirmed="confirmDeletePeerQuestion"
          optionalText="question"
        />
        <ConnectIntegrationsAssessmentModal
          v-if="show_integrations_modal"
          @close="show_integrations_modal = false"
          @goToInviteStudents="showShareRoundtableModalIntegrationsTab"
        />
        <ModalSampleRoundTable
          v-if="isModalSampleOpen"
          @close="isModalSampleOpen = false"
          :dataSample="urlVideoSample"
        />
        <StudentSettings
          v-if="show_student_settings"
          :submission="submission"
          @close="show_student_settings = false"
        />
        <EditRoundtableModal
          v-if="isEditRTModalOpen"
          :id="getCurrentRT.id" 
          @close="isEditRTModalOpen = false"
        />
        <AreYouSureChangeTab
          v-if="
            openOnChangeSubmission && getMySubmission.prompt_response !== null
          "
          @close="openOnChangeSubmission = false"
          @changeTabAndLeaveChanges="leaveSubmissionChanges"
          @closeModal="() => (isSubmissionChanged = false)"
          @updateDiscussion="goToDiscussion"
          :route_to_change="selectedTabBeforeLeave"
          :submissionResponse="changingSubmissionOnEditor"
          :wordCount="wordCountFromSubmission"
        />
        <UnsubmittedDraftResponse
          v-if="isUnsubmittedResponseOpen"
          :studentSubmission="selectedUnsubmmitedSubmission"
          @close="isUnsubmittedResponseOpen = false"
        />

        <LeaveEditSubmissionModal
          v-if="show_leave_edit_submission === true"
          @close="handleCloseLeaveSubmission()"
          @updateDiscussion="goToDiscussion"
          :route_to_go="route_selected"
          :tab_to_go="selectedTabBeforeLeave"
          :submissionResponse="changingSubmissionOnEditor"
        />
        <roundTable-welcome-modal
          activityType="online"
          @close="closeRTWelcomeModal"
          v-if="show_RT_Welcome_modal"
          :data="welcomeRoundTableData"
        />
        <InviteStudentsModal
          v-if="isInviteStudentModalOpen"
          @close="isInviteStudentModalOpen = false"
        />
        <NewRoundTable
          v-if="is_new_roundtable_open"
          :outside_option="outside_option"
          :follow_up_from="getCurrentRT"
          @close="is_new_roundtable_open = false"
        />
        <TemplatesModal
          v-if="templates_modal"
          :activityType="'written'"
          @close="closeTemplateModal"
        />
        <DraftEvaluationsModal
          v-if="show_draft_evalutions"
          :draftEvaluations="draft_evaluations"
          @close="show_draft_evalutions = false"
        />
        <GenieAssessmentConfirmModal
          v-if="is_to_show_genie_grader_confirm === true"
          :type_genie_assessment="type_genie_assessment"
          :roundtable="getCurrentRT"
          :profile="getProfile"
          @close="is_to_show_genie_grader_confirm = false"
        />
        <SaveSettingsModal
          v-if="is_settings_confirm_modal_open"
          @close="handleCloseSaveSettings()"
          @handleSaveSettings="saveSettingsTab"
        />
        <GenieFeedbackModal
          v-if="is_genie_feedback_modal_open === true"
          :submission="submission_genie_feedback"
          @close="is_genie_feedback_modal_open = false"
        />
        <EditFollowUpQuestions
          v-if="edit_follow_up_questions_modal"
          @close="edit_follow_up_questions_modal = false"
        />
        <Draft 
          v-if="isFollowUpOpen === true" 
          :id="selectedRoundTableToFollow.draftId" 
          :type="selectedRoundTableToFollow.type"
          :isFollowUp="true" 
          @close="isFollowUpOpen = false"
        />
        <!-- <CreateSequence 
          v-if="isFollowUpOpen === true" 
          :id="selectedRoundTableToFollow.id"
          :draftId="selectedRoundTableToFollow.draftId" 
          :type="selectedRoundTableToFollow.type"
          :course="selectedRoundTableToFollow.course"
          :currentRoundtableType="selectedRoundTableToFollow.currentRoundtableType" 
          @close="isFollowUpOpen = false" 
        /> -->
      </div>
    </div>
    <div
      v-if="hasTeacherPermissionsInCourse && showGeniePopover && getCourseDetail.id !== '151035ea-b4c7-484d-a07d-3505fa895cf7'"
      class="fixed bottom-4 z-40 shadow-xl rounded-lg bg-white border border-solid border-gray-200 w-96 transition-all duration-1000 ease-in-out transform"
      :class="{
        'opacity-100 scale-100': showGeniePopover,
        'opacity-0 scale-75': !showGeniePopover
      }"
      style="left: 65px;"
    >
      <div class="flex flex-col space-y-5 px-5 pt-2.5 pb-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2.5">
            <i class="fa-regular fa-lightbulb text-base text-yellow-500"></i>
            <h6 class="font-poppins font-semibold text-gray-800">Parlay Tip</h6>
          </div>
          <button
            @click="handleCloseGeniePopover"
            class="w-8 h-8 rounded-full hover:bg-gray-100 hover:text-primary"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="w-full relative">
          <img v-if="genie_teaching_page === 1" src="/static/genie_feedback4.png" />
          <img v-if="genie_teaching_page === 2" src="/static/genie_feedback2.png" />
          <img v-if="genie_teaching_page === 3" src="/static/genie_feedback3.png" />
        </div>
        <div class="flex flex-col space-y-2">
          <h4 class="font-poppins font-semibold text-base text-gray-800">
            Have you tried Genie Feedback for students?
          </h4>
          <p
            v-if="genie_teaching_page === 1"
            class="font-poppins font-light text-gray-600"
          >
            Before students submit, they will be able to ask the Genie for feedback
            according to the RoundTable prompt and criteria.
          </p>
          <p
            v-else-if="genie_teaching_page === 2"
            class="font-poppins font-light text-gray-600"
          >
            You will be able to see the feedback each student received and how they
            changed their submission.
          </p>
          <p
            v-else-if="genie_teaching_page === 3"
            class="font-poppins font-light text-gray-600"
          >
            Turn Genie Feedback <span class="font-semibold text-gray-800">ON</span>
            in your Settings.
          </p>
        </div>
        <div class="flex justify-between items-center">
          <button
            :disabled="genie_teaching_page <= 1"
            @click="handleChangePageTeaching('back')"
            class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200"
          >
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <div class="flex items-center space-x-2">
            <div
              class="w-3 h-3 rounded-full bg-blueGray-500"
              :class="{'bg-blueGray-800': genie_teaching_page === 1}"
            ></div>
            <div
              class="w-3 h-3 rounded-full bg-blueGray-500"
              :class="{'bg-blueGray-800': genie_teaching_page === 2}"
            ></div>
            <div
              class="w-3 h-3 rounded-full bg-blueGray-500"
              :class="{'bg-blueGray-800': genie_teaching_page === 3}"
            ></div>
          </div>
          <button
            :disabled="genie_teaching_page >= 3"
            @click="handleChangePageTeaching('next')"
            class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200"
          >
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
    <NewInstruction v-if="isNewInstructionOpen === true" @close="isNewInstructionOpen = false"/>
  </DefaultLayout>
</template>

<script>
  import Vue from 'vue';
  import { mapGetters } from 'vuex';
  import store from 'store';
  import $ from 'jquery';
  // MIXINS
  import onlineRoundtableSockets from 'mixins/sockets/onlineRoundtableSocketEvents';
  import courseSocketMixin from 'mixins/sockets/courseSocketEvents';
  // EVENT BUS
  import { EventBus } from 'utils/event-bus';
  import moment from 'moment';

  import getWelcomeSettings from 'services/account/getWelcomeSettings';

  import RoundTableWelcomeModal from 'components/modals/roundTableWelcomeModal';
  import settingsTab from './tabs/settingsTab';
  import discussionTab from './tabs/discussionTab';
  import summaryTab from './tabs/summaryTab';
  import individual_summaryTab from './tabs/IndividualSummary.vue';
  import SyncAssessmentDataModal from 'components/modals/syncAssessmentDataModal';
  import CloseDateModal from 'components/modals/closeDateModal';
  import IndividualCommentOverlay from './components/individualCommentOverlay';
  import IndividualReplyOverlay from './components/individualReplyOverlay';
  import IndividualSubmissionOverlay from './components/individualSubmissionOverlay';
  import IndividualAssessmentOverlay from './components/individualAssessmentOverlay';
  import RoundtableSummaryOverlay from './components/roundtableSummary/roundtableSummaryOverlay';
  import integrationsConnectModal from 'components/modals/integrationsConnectModal';
  import googleClassroomModal from 'components/modals/googleClassroomModal';
  import integrationsUpgradeModal from 'components/modals/integrationsUpgradeModal';
  import integrationConnectedModal from 'components/modals/shareActivityModalViews/integrationConnectedModal';
  import createNewPointModal from 'components/createNewPointModal.vue';
  import { AcceptedOutsideOptions } from '../../components/modals/New_RoundTable/Defaults.js';
  import OnlineStudentsAssessment from './components/onlineStudentsAssessment';
  import TemplatesModal from 'components/modals/templatesModal.vue';
  import DraftEvaluationsModal from 'components/modals/draftEvaluationsModal.vue';
  import GenieRecapSideBar from './components/genieRecap/genieRecapSideBar.vue';
  import EditFollowUpQuestions from 'components/modals/editFollowUpQuestionsModal/editFollowUpQuestions.vue';

  import googleClassroomService from 'services/googleClassroom/googleClassroomServices';
  import roundtableService from 'features/onlineRoundtables/onlineRoundtables.services';
  import updateUser from 'services/account/updateUser';
  import CreateSequence from '../Sequences/Index.vue';
  import Draft from '../Drafts/Index.vue';
  import { WrittenFollowUpTemplate } from '../Sequences/Templates/WrittenFollowUpTemplate.js';
  import NewInstruction from '../../components/Dialogs/InstructionsFeedback/InstructionsFeedback.vue';
  import Axios from 'axios';

  export default Vue.extend({
    components: {
      CreateSequence,
      Draft,
      OnlineStudentsList: () => import('./components/onlineStudentList/OnlineStudentsList.vue'),
      TabsSelectTest: () => import('../../features/dashboard/TabsRoleSelect.vue'),
      DefaultLayout: () => import('layouts/default/default.vue'),
      SubmissionForm: () => import('features/onlineRoundtables/components/submissionForm/submissionForm.vue'),
      Tooltip: () => import('../../components/Tooltip.vue'),
      ModalNonRequiredSubmission: () => import('./Modals/NonSubmissionRequired.vue'),
      EditRoundtableModal: () => import('../WrittenRoundtables/Dialogs/EditWrittenRoundTable.vue'),
      AreYouSureChangeTab: () => import('./Modals/SureOnChangeTab.vue'),
      LeaveEditSubmissionModal: () =>
        import('./Modals/LeaveEditSubmission.vue'),
      UnsubmittedDraftResponse: () =>
        import('./Modals/UnsubmittedResponses.vue'),
      ShareActivityModal: () => import('components/modals/shareActivityModal'),
      CloseDateModal,
      DuplicateRoundTableModal: () =>
        import(
          'components/modals/duplicateRoundtableModal/duplicateRoundtableModal'
        ),
      IndividualCommentOverlay,
      IndividualReplyOverlay,
      IndividualSubmissionOverlay,
      IndividualAssessmentOverlay,
      RoundtableSummaryOverlay,
      integrationsConnectModal,
      googleClassroomModal,
      integrationsUpgradeModal,
      integrationConnectedModal,
      OnlineRoundableMenu: () => import('./components/onlineRoundtableMenu'),
      PromptTab: () => import('./tabs/promptTab'),
      settingsTab,
      discussionTab,
      summaryTab,
      individual_summaryTab,
      createNewPointModal,
      DialogDeleteConfirm: () => import('components/dialogDeleteConfirm'),
      ConnectIntegrationsAssessmentModal: () =>
        import('components/modals/connectIntegrationsAssessmentModal'),
      IdentityReveal: () => import('components/identityReveal/identityReveal'),
      SyncAssessmentDataModal,
      StudentSettings: () =>
        import('features/courses/pages/settings/studentSettings.vue'),
      CloseRoundTableModal: () => import('./Modals/CloseDiscussion.vue'),
      RoundTableWelcomeModal,
      ModalSampleRoundTable: () =>
        import('../../components/ModalSampleRoundTable.vue'),
      InviteStudentsModal: () =>
        import('../../components/modals/InviteStudents/InviteStudents.vue'),
      BreadCrumbSequence: () =>
        import(
          '../../features/courses/components/Sequences/Breadcrumb/SequenceCrumb.vue'
        ),
      DropdownMenu: () => import('./components/Dropdown/DropdownMenu.vue'),
      NewRoundTable: () =>
        import('components/modals/New_RoundTable/NewRoundTable.vue'),
      OnlineStudentsAssessment,
      SaveSettingsModal: () =>
        import('components/modals/SaveSettingsConfirm/SaveSettingsModal.vue'),
      TemplatesModal,
      DraftEvaluationsModal,
      GenieAssessmentConfirmModal: () =>
        import('./Modals/GenieAssessmentConfirmation.vue'),
      GenieRecapSideBar,
      GenieFeedbackModal: () => import('./Modals/GenieFeedbackByStudent.vue'),
      EditFollowUpQuestions,
      NewInstruction
    },
    data() {
      return {
        is_sequence_menu_open: false,
        pre_selected_tab_in_share_modal: '',
        isEnableEditSubmission: false,
        show_introduction_modal: false,
        isEditRTModalOpen: false,
        wordCountFromSubmission: '',
        show_share_roundtable_modal: false,
        wordCount: 0,
        show_RT_Welcome_modal: false,
        show_close_date_modal: false,
        show_duplicate_modal: false,
        show_end_reopen_modal: false,
        show_integrations_modal: false,
        openOnChangeSubmission: false,
        leaveChanges: false,
        selectedUnsubmmitedSubmission: {},
        isUnsubmittedResponseOpen: false,
        tabToGoBeforeChange: '',
        show_student_settings: false,
        confirm: {
          title: 'Interesting...',
          content: 'Something is a little odd, please refresh and try again!',
          ok: 'Ok',
          cancel: 'Cancel',
        },
        changingSubmissionOnEditor: '',
        user_has_changes_to_save: false,
        in_viewport: [],
        show_overlay: '', // 'submission, comment, reply'
        welcomeRoundTableData: null,
        scrollToInOverlay: '',
        hard_close: false,
        checkRTsEndedCron: null,
        open_google_classroom_dialog: false,
        goToOverlay: false,
        integration: {},
        is_integration_connected: false,
        tab: 'prompt',
        roundtables_with_point: [],
        selected_point_tag: null,
        show_point_creation_modal: false,
        show_alert_assessment_changing: false,
        show_delete_point_modal: false,
        show_edit_question_modal: false,
        show_delete_question_modal: false,
        peer_assessment_questions: [],
        question_to_edit_index: -1,
        componentKey: 0,
        delete_point_index: -1,
        activityTypeOverride: null,
        delete_question_index: -1,
        toggle_responses: true,
        windowWith: window.innerWidth,
        sync_assessment: false,
        show_sync_assessment_modal: false,
        integrationName: '',
        mySubmission: null,
        isLoadingSubmission: false,
        submissionIsStartedStudent: false,
        isModalNonRequiredSubmissionOpen: false,
        isGoingToObvserver: false,
        isSubmissionRequiredOnRoundTable: false,
        isByPassedDateRoundTable: null,
        isMountingComponent: false,
        submission: {},
        isSubmissionStarted: false,
        isSubmissionChanged: false,
        isToChangeTab: false,
        teacherSubmission: {},
        selectedTabBeforeLeave: '',
        showQuestionIcon: false,
        isSubmissionChange: false,
        isToLeaveChanges: false,
        isLoadingWritten: true,
        isModalSampleOpen: false,
        urlVideoSample: '',
        show_welcome_modal: false,
        isModalTeacherOpenAfterSignUp: false,
        is_button_save_setting_enable: false,
        isSavingDraftResponse: false,
        draftResponseMessage: '',
        isDraftResponseSave: true,
        show_leave_draft_response_modal: false,
        isEditedResponseSave: true,
        show_leave_edit_submission: false,
        route_selected: {},
        isInviteStudentModalOpen: false,
        members_course_socket: null,
        sequence_roundtables: [],
        is_new_roundtable_open: false,
        outside_option: '',
        open_assessment: false,
        orderedSubmissions: [],
        templates_modal: false,
        show_draft_evalutions: false,
        draft_evaluations: [],
        is_to_show_genie_grader_confirm: false,
        show_tab_menu: false,
        width_right_grid: 0,
        is_settings_confirm_modal_open: false,
        genie_recap: false,
        is_genie_feedback_modal_open: false,
        submission_genie_feedback: {},
        edit_follow_up_questions_modal: false,
        selected_point_scale: null,
        genie_teaching_page: 1,
        showGeniePopover: false,
        isFollowUpOpen: false,
        selectedRoundTableToFollow: null,
        isNewInstructionOpen: false,
      };
    },
    mixins: [onlineRoundtableSockets, courseSocketMixin],
    computed: {
      ...mapGetters([
        'getCurrentRT',
        'getCurrentRTLoading',
        'getCourseDetail',
        'getCourseLoading',
        'hasTeacherPermissionsInCourse',
        'getStudentsInCourse',
        'getOrderedSubmissions',
        'getSubmissionsBySubmitStatus',
        'getMyRTUserDetails',
        'getOrderedOnlineRTs',
        'getProfile',
        'getProviders',
        'getRoundtableAuthor',
        'getMySubmission',
        'getCourses',
      ]),
      current_sequence_number: {
        get: function () {
          const position = this.getCurrentRT.roundtables_sequence.findIndex(
            (roundtable) => roundtable.id === this.$route.params.roundtableID
          );
          return position;
        },
      },
      currentSubmission: function () {
        const current_submission = this.getCurrentRT.submissions.find(
          (s) => s.id === this.getMyRTUserDetails.submission_id
        );
        return current_submission;
      },
      isStudentStartSubmission: function () {
        return (
          this.getMyRTUserDetails.submission_is_started === true &&
          !this.hasTeacherPermissionsInCourse &&
          this.getCurrentRT.require_submission === true
        );
      },
      isRoundTableSample: function () {
        return this.getCurrentRT.is_sample;
      },
      setSelectedTabStyle: function () {
        return 'pb-2.5 text-primary border-solid border-b-2 border-primary text-sm text-gray-400 font-semibold cursor-pointer mr-7 border-0';
      },
      setNonSelectedTabStyle: function () {
        if (this.hasTeacherPermissionsInCourse) {
          return 'pb-2.5 text-sm text-gray-400 font-semibold cursor-pointer mr-7 border-0';
        } else {
          if (
            this.getCurrentRT.require_submission === true &&
            this.isStudentStartSubmission === false
          ) {
            return 'pb-2.5text-sm text-gray-300 font-semibold cursor-pointer mr-7 border-0';
          } else {
            return 'pb-2.5 text-sm text-gray-400 font-semibold cursor-pointer mr-7 border-0';
          }
        }
      },
      gridLeft: function () {
        /* Apply Style on Left Side of Written RoundTable */
        if (this.tab === 'prompt') {
          if (this.hasTeacherPermissionsInCourse) return 'w-full h-full';
          else if (
            this.hasTeacherPermissionsInCourse === false &&
            this.isRoundtableEnded === true &&
            this.getCurrentRT.hard_close === true
          ) {
            return 'w-full';
          } else {
            return 'xl:w-6/12 lg:w-6/12';
          }
        } else if (this.tab === 'discussion') {
          /* Toggle Response Verification is Active */
          if (this.toggle_responses)
            return 'sm:w-full md:w-full lg:w-7/12 xl:w-8/12 2xl:w-9/12';
          else return 'w-full';
        } else return 'w-full';
        /* Apply in the other tabs */
      },
      gridRight: function () {
        /* Apply Style on Right Side of Written RoundTable */
        if (this.tab === 'prompt') {
          if (this.hasTeacherPermissionsInCourse) {
            return 'w-full';
          } else {
            return 'xl:w-6/12 lg:w-6/12';
          }
        } else if (this.tab === 'discussion') {
          return 'sm:w-full md:w-full lg:w-5/12 xl:w-4/12 2xl:w-3/12';
        }
      },
      isRoundtableEnded: function () {
        return moment().isAfter(this.getCurrentRT.end_date);
      },
      showSubmissionContainer: function () {
        if (this.hasTeacherPermissionsInCourse) {
          if (
            this.tab === 'prompt' &&
            (this.$route.query.startSubmission ||
              this.getMyRTUserDetails.submission_is_started === true)
          ) {
            return true;
          }
        } else if (!this.hasTeacherPermissionsInCourse) {
          if (this.tab === 'prompt' && this.isRoundtableEnded === false) {
            return true;
          } else if (
            this.tab === 'prompt' &&
            this.isRoundtableEnded === true &&
            this.getCurrentRT.hard_close === false
          ) {
            return true;
          } else if (
            this.tab === 'prompt' &&
            this.isRoundtableEnded === true &&
            this.getCurrentRT.hard_close === true
          ) {
            return false;
          }
        }
        return false;
      },
      showRightContainer: function () {
        if (this.tab === 'discussion' && this.toggle_responses) {
          return true;
        } else if (this.tab === 'prompt') {
          if (this.hasTeacherPermissionsInCourse) {
            if (
              this.$route.query.startSubmission ||
              this.getMyRTUserDetails.submission_is_started === true
            ) {
              return true;
            }
          } else if (!this.hasTeacherPermissionsInCourse) {
            if (this.tab === 'prompt' && this.isRoundtableEnded === false) {
              return true;
            } else if (
              this.tab === 'prompt' &&
              this.isRoundtableEnded === true &&
              this.getCurrentRT.hard_close === false
            ) {
              return true;
            } else if (
              this.tab === 'prompt' &&
              this.isRoundtableEnded === true &&
              this.getCurrentRT.hard_close === true
            ) {
              return false;
            }
          }
        }
        return false;
      },
      roundTableCloseDate: function () {
        return this.getCurrentRT.end_date
          ? moment(this.getCurrentRT.end_date).format('MMM Do [at] h:mm a')
          : '';
      },

      rt_integration() {
        const google_integration =
          this.getCurrentRT.google_classroom_course_work_id;
        if (google_integration) {
          return this.getCurrentRT.google_classroom_course_work_url;
        }
        const integrations = this.getCurrentRT.edlink_integrations;
        if (integrations && integrations.length) {
          return integrations[0];
        }
      },
      assignmentUrl() {
        const google_integration =
          this.getCurrentRT.google_classroom_course_work_id;
        if (google_integration) {
          return this.getCurrentRT.google_classroom_course_work_url;
        }
        return this.rt_integration ? this.rt_integration.url : null;
      },
      assignmentIcon() {
        if (this.rt_integration) {
          const name = this.rt_integration.provider_name || 'Google Classroom';
          if (name === 'Google Classroom')
            return '/static/google_class_round.png';
          if (this.rt_integration.provider_name === 'Schoology')
            return '/static/integrations/Schoology.png';
          if (this.rt_integration.provider_name === 'Canvas')
            return '/static/integrations/Canvas.png';
          if (this.rt_integration.provider_name === 'Microsoft')
            return '/static/integrations/Microsoft.png';
          if (this.rt_integration.provider_name === 'Brightspace')
            return '/static/integrations/Brightspace.png';
          const provider = this.getProviders.find(
            (p) =>
              p.name.toLowerCase() ===
              this.rt_integration.provider_name.toLowerCase()
          );
          return provider ? provider.icon_url : null;
        }
      },
      activityType: {
        get() {
          if (this.activityTypeOverride) return this.activityTypeOverride;
          if (this.$route.params.roundtableID) return 'written';
          if (this.$route.params.liveDiscussionID) return 'verbal';
          return null;
        },
        set(value) {
          this.activityTypeOverride = value;
        },
      },
      currentActivity() {
        if (this.activityType === 'written') return this.getCurrentRT;
        return this.getCurrentDiscussion;
      },
      courseWorkId() {
        if (this.$route.params.roundtableID)
          return this.getCurrentRT.google_classroom_course_work_id;
        if (this.$route.params.liveDiscussionID)
          return this.getCurrentDiscussion.google_classroom_course_work_id;
        return null;
      },
      integrationConnected() {
        if (this.show_sync_assessment_modal) {
          if (
            this.currentActivity.edlink_integrations
              .map((ed) => ed.provider_name.toLowerCase())
              .includes(this.selected_integration)
          )
            return 3;
          if (
            this.getCourseDetail.edlink_integrations
              .map((ed) => ed.provider_name.toLowerCase())
              .includes(this.selected_integration)
          )
            return 2;
          return 1;
        }
        return 0;
      },
      first_in_viewport() {
        return [this.in_viewport[0]];
      },
      component_to_render() {
        return this.tab + 'Tab';
      },
      author_name() {
        return (this.getRoundtableAuthor.preferred_name || this.getRoundtableAuthor.name);
      },
      author_avatar() {
        return (this.getRoundtableAuthor.avatar_url || this.getRoundtableAuthor.avatar_url);
      },
      isFromNotificationState() {
        return store.state.isFromNotification;
      },
    },
    watch: {
      // '$route.query': function () {
      //   this.handleShowOverlayRouteQuery();
      //   this.forceRerender();
      // },
      '$route.name': async function (newValue, old) {
        this.draftResponseMessage = '';
        this.isEnableEditSubmission = false;
        if (this.mySubmission !== null) {
          this.changingSubmissionOnEditor = this.mySubmission.prompt_response;
          if (old === 'roundtable.prompt') {
            this.leaveChanges = false;
          }
        }
      },
      '$route.query': function() {
        this.handleShowOverlayRouteQuery();
      },
      members_course_socket: function (newValue) {
        this.members_channel = newValue;
      },
      'getSubmissionsBySubmitStatus.submitted.length': function (newVal) {
        if (newVal === 0) {
          this.genie_recap = false;
        }
      },
    },
    created() {
      window.addEventListener('resize', this.onResize);
    },

    methods: {
      handleCloseSaveSettings: function() {
        this.is_settings_confirm_modal_open = false;
        if (this.route_selected.tab_to_go) {
          this.is_button_save_setting_enable = true;
          this.forceRerender();
          this.goToTab(this.route_selected.tab_to_go);
        }
      },
      handleCloseGeniePopover: async function() {
        try {
          this.showGeniePopover = !this.showGeniePopover;
          const { data } = await updateUser(this.getProfile.id, { genie_feedback_feature_flag: true });
          await this.$store.dispatch('setAccount', data);
        } catch (error) {
          console.log(error);
        }
      },
      handleChangePageTeaching: function(page) {
        if (page === "back" && this.genie_teaching_page === 1) {
          return;
        }
        if (page === "next" && this.genie_teaching_page > 3) {
          return;
        }
        if (page === "next") {
          this.genie_teaching_page = this.genie_teaching_page + 1;
        } else if(page === "back") {
          this.genie_teaching_page = this.genie_teaching_page - 1;
        }
      },
      handleUpdateSubmissionWithClassroomId: async function() {
        if (this.getCurrentRT.google_classroom_course_work_id && this.getMySubmission.google_classroom_integration && this.getMySubmission.google_classroom_integration.connected && !this.getMySubmission.google_classroom_integration.submission_id) {
          const { data } = await googleClassroomService.getGoogleClassroomAssignmentSubmissions(
            this.getCourseDetail.id,
            this.getCourseDetail.google_classroom_integration.course_id,
            'written',
            this.getCurrentRT.id,
            this.getCurrentRT.google_classroom_course_work_id,
          );

          const { studentSubmissions } = data;
          const submission = studentSubmissions[0];
          const newClassroomSubmission = { connected: true, submission_id: submission.id };
          await roundtableService.editSubmission({ google_classroom_integration: newClassroomSubmission }, this.getMySubmission.id).then((res) => {
            this.$store.commit('EDIT_SUBMISSION_CONTENT', res.data);
            return res;
          });
          
        }
      },
      handleClickSequenceRoundTable: async function (
        roundtable_sequence_id,
        roundtable_sequence_type
      ) {
        if (
          roundtable_sequence_type === 'written' ||
          roundtable_sequence_type === 'roundtable'
        ) {
          const route = {
            name: 'roundtable.index',
            params: {
              roundtableID: roundtable_sequence_id,
              courseID: this.$route.params.courseID,
            },
          };
          this.$router.push(route);
          this.$store.commit('setIsReloadComponent');
          return;
        }
        if (
          roundtable_sequence_type === 'verbal' ||
          roundtable_sequence_type === 'live_discussion'
        ) {
          const route = {
            name: 'live-discussion.index',
            params: {
              liveDiscussionID: roundtable_sequence_id,
              courseID: this.$route.params.courseID,
            },
          };
          this.$router.push(route);
          return;
        }
      },
      handleOpenSequenceMenu: async function () {
        this.is_sequence_menu_open = !this.is_sequence_menu_open;
        return;
      },
      handleEditPrompt: function () {
        this.$router.replace({
          name: this.$route.name,
          params: this.$route.params,
        });
        this.isEditRTModalOpen = true;
      },
      goToDiscussion: function () {
        this.isSubmissionChange = false;
        this.goToTab('discussion');
      },
      handleSubmissionChange: function (value) {
        this.isSubmissionChange = value;
      },
      leaveSubmissionChanges: function (value) {
        if (value === true) {
          this.isToLeaveChanges = value;
          this.goToTab(this.selectedTabBeforeLeave);
        }
      },
      unsubmittedDraftResponse: function (submission) {
        this.isUnsubmittedResponseOpen = true;
        this.selectedUnsubmmitedSubmission = submission;
      },
      handleShowOverlayRouteQuery: function () {
        const {
          submissionID,
          recapID,
          topLevelCommentID,
          replyCommentID,
          assessment,
          tab,
        } = this.$route.query;
        if (submissionID) {
          store
            .dispatch('submissionClicked', submissionID)
            .then(() => (this.goToOverlay = true))
            .catch((error) => {
              if (error.response.status === 403) {
                this.goToOverlay = false;
                this.show_overlay = '';
                /* Verificar se é pra adicionar toast de erro! */
                EventBus.$emit('toast-msg', error.response.data.error);
              }
              return;
            });
        }
        if (submissionID && assessment) {
          this.getSubmissionSummary(submissionID);
          this.show_overlay = 'assessment';
          this.$store.dispatch('userEnteredSummaryOverlay', true);
        } else if (
          submissionID &&
          !topLevelCommentID &&
          !replyCommentID &&
          !tab
        ) {
          this.show_overlay = 'submission';
          this.$store.dispatch('userEnteredSummaryOverlay', true);
        } else if (submissionID && topLevelCommentID && !replyCommentID) {
          this.show_overlay = 'comment';
          this.$store.dispatch('userEnteredSummaryOverlay', true);
        } else if (submissionID && topLevelCommentID && replyCommentID) {
          this.show_overlay = 'reply';
          this.$store.dispatch('userEnteredSummaryOverlay', true);
        } else if (submissionID && tab) {
          EventBus.$emit('scrollToSubmission', submissionID);
        } else if (recapID && topLevelCommentID && replyCommentID) {
          this.show_overlay = 'reply';
          this.$store.dispatch('userEnteredSummaryOverlay', true);
        }
      },
      isPromptChanging: function (value) {
        this.changingSubmissionOnEditor = value;
      },
      // setFullscreenSubmissionOnPrompt: function () {
      //   /* Get Button Element Inside Toolbar TinyMCE */
      //   const element = document.querySelector('[aria-label="Fullscreen"]');
      //   element.click();
      // },
      handleCloseLeaveSubmission: function () {
        this.show_leave_edit_submission = false;
        this.selectedTabBeforeLeave = '';
        this.route_selected = '';
      },
      onResize() {
        const element = document.getElementById('rigthGrid');
        const width = element.offsetWidth;
        this.width_right_grid = width;

        this.windowWith = window.innerWidth;
        if (this.windowWith < 1024) {
          this.toggle_responses = false;
        }
        if (this.tab === 'prompt') {
          if (width < 700) {
            this.show_tab_menu = true;
          }
          if (width > 701) {
            this.show_tab_menu = false;
          }
        }

        if (this.tab !== 'prompt') {
          if (this.windowWith < 1024) {
            this.show_tab_menu = true;
          }
          if (this.windowWith > 1024) {
            this.show_tab_menu = false;
          }
        }
      },
      getSubmissionSummary(submissionID) {
        return store
          .dispatch('getSubmissionSummary', submissionID)
          .catch((e) => console.error(e));
      },
      async selectSubmission(
        submissionID,
        is_genie_grader_running,
        teacher_submission
      ) {
        if (is_genie_grader_running) return;
        EventBus.$emit('scrollToSubmission', submissionID);
        if (
          !teacher_submission &&
          (this.hasTeacherPermissionsInCourse ||
            submissionID === this.getMyRTUserDetails.submission_id)
        ) {
          await this.getSubmissionSummary(submissionID);
          this.open_assessment = true;
        }
      },
      revealNames() {
        store.dispatch('toggleRevealNames', !this.getCurrentRT.reveal_names);
      },
      closeShareModal() {
        this.pre_selected_tab_in_share_modal = '';
        this.show_share_roundtable_modal = false;
      },
      closeRTWelcomeModal() {
        this.show_RT_Welcome_modal = false;
      },
      showInviteLink() {
        this.pre_selected_tab_in_share_modal = 'magicLink';
        this.show_share_roundtable_modal = true;
      },
      showShareRoundtableModal() {
        this.getStudentsInCourse.length
          ? (this.isInviteStudentModalOpen = true)
          : this.showInviteLink();
      },
      showShareRoundtableModalIntegrationsTab: function () {
        this.pre_selected_tab_in_share_modal = 'integrations';
        this.getStudentsInCourse.length
          ? (this.isInviteStudentModalOpen = true)
          : this.showInviteLink();
      },
      showFirstRTWelcomeModal() {
        getWelcomeSettings.getSettingsByQuery('online').then((data) => {
          let videos = data.filter(
            (e) => e.video_url && e.video_url.length > 0
          );
          this.welcomeRoundTableData = data;
          if (videos.length > 0) {
            this.show_RT_Welcome_modal = true;
          }
        });
      },
      showWelcomeVideosOnSamples: function () {
        if (
          this.$route.params.courseID === this.$store.state.course_sample_id
        ) {
          /* ID of Course Sample and Roundtable Sample */
          if (
            this.$route.name === 'roundtable.index' &&
            this.$route.params.roundtableID ===
              'e080cbb4-878b-49ea-a83c-3fa585e11a02'
          ) {
            getWelcomeSettings
              .getSettingsByQuery('sample_written')
              .then((res) => {
                this.isModalSampleOpen = true;
                this.urlVideoSample = res[0];
              });
          }
        }
      },
      setUpSubmissionAuthorHighlighting() {
        // Check if the card is inside the viewport everytime the user scrolls or
        // risizes the feed-body then saves the div id and sends
        // it to the child (OnlineStudentList) to highlight it
        $('.vue-recycle-scroller').on('resize scroll', () => {
          this.in_viewport = [];
          $('.feed-card').each((i, obj) => {
            if (this.isInViewport(obj)) {
              this.in_viewport.indexOf(obj.id) === -1
                ? this.in_viewport.push(obj.id)
                : null;
            }
          });
        });
      },
      openToggleEndRoundtableConfirm() {
        if (this.hasTeacherPermissionsInCourse) {
          if (this.isRoundtableEnded) {
            this.confirm.title = 'Do you want to continue this RoundTable?';
            this.confirm.content =
              'All comments that your students have submitted since ending \n' +
              'the RoundTable will be included in their summary metrics.';
          } else {
            this.confirm.title = 'Do you want to close this RoundTable?';
            this.confirm.content =
              'All student contributions submitted after the RoundTable is closed will be marked as "submitted after close".';
          }
          this.show_end_reopen_modal = true;
        }
      },
      handleCloseRoundTable: function (value) {
        this.hard_close = value;
        this.closeBasicConfirm('ok');
      },
      closeBasicConfirm(dialogAnswer) {
        const endDate = moment();
        switch (this.confirm.title) {
          case 'Do you want to close this RoundTable?':
            dialogAnswer === 'ok'
              ? this.changeRoundtableEndDate(endDate)
              : null;
            break;
          case 'Do you want to continue this RoundTable?':
            dialogAnswer === 'ok' ? this.changeRoundtableEndDate(null) : null;
            break;
          default:
            null;
        }
        this.show_end_reopen_modal = false;
      },
      changeRoundtableEndDate(endDate) {
        const unsuccessfulMessage = endDate
          ? 'RoundTable did not end. Try again.'
          : 'RoundTable did not continue. Try again.';
        return store
          .dispatch('savingRTDetails', {
            id: this.getCurrentRT.id,
            end_date: endDate,
            hard_close: this.hard_close,
            is_ended: Boolean(endDate),
          })
          .then(() => {
            /* Verificar se esta Toast é para manter!! */
            const msg = endDate
              ? 'Your RoundTable has successfully ended'
              : 'Your RoundTable lives again!';
            EventBus.$emit('toast-msg', msg);
          })
          .catch((err) => {
            /* Verificar se esta Toast é para manter!! */
            EventBus.$emit('toast-msg', unsuccessfulMessage);
            console.error(err);
          });
      },
      /* Vou deixar ficar mas não deve estar a ser usado */
      isInViewport(obj) {
        const elementTop = $(obj).offset().top;
        const elementBottom = elementTop + $(obj).outerHeight();
        const viewportTop = $('#feed-body').offset().top;
        const viewportBottom =
          viewportTop + $('#feed-body').outerHeight() * 0.5;
        return elementBottom > viewportTop && elementTop < viewportBottom;
      },
      /* Não está a ser usado verificar se tem implicações */
      /* goToFeed() {
        this.$router.push({ name: 'roundtable.discussion' });
        this.show_introduction_modal = true;
      }, */
      listenToRoundtableEndings: function () {
        this.checkRTsEndedCron = setInterval(() => {
          let updatedRTs = false;
          const updateRTs = JSON.parse(
            JSON.stringify(this.getOrderedOnlineRTs)
          );
          if (this.getOrderedOnlineRTs && this.getOrderedOnlineRTs.length) {
            updateRTs.map((newRT) => {
              // If RT has end date is already ended or will end in date
              if (newRT && newRT.end_date) {
                newRT.is_ended = new Date(newRT.end_date) <= new Date();
                if (
                  this.getCurrentRT.id === newRT.id &&
                  newRT.is_ended !== this.getCurrentRT.is_ended
                ) {
                  updatedRTs = true;
                  this.$store.commit('UPDATE_RT_DETAILS', {
                    is_ended: newRT.is_ended,
                    hard_close: newRT.hard_close,
                  });
                }
              }
              return newRT;
            });
            if (updatedRTs) {
              store.commit('UPDATE_COURSE_DETAIL', {
                roundtables: updateRTs,
              });
            }
          }
        }, 1000);
      },
      checkRoundtablesWithPoint: function (tag) {
        this.selected_point_tag = tag;
        const { roundtables } = this.getCourseDetail;
        let result = [];
        for (let index = 0; index < roundtables.length; index += 1) {
          const rt = roundtables[index];
          const { peer_rewards } = rt.points_settings;
          const has_point = peer_rewards.find(
            (p) => p.tag === this.selected_point_tag
          );
          if (has_point) {
            result.push({ title: rt.title, id: rt.id });
          }
        }
        this.roundtables_with_point = result;
        if (this.roundtables_with_point.length && this.getProfile.is_criteria_editing_message_disabled === false) {
          this.show_alert_assessment_changing = true;
        }
        return result;
      },
      editAssessmentPoint: function (value, tag, point_scale) {
        this.activityType = value;
        this.selected_point_scale = point_scale;
        if (tag) {
          this.checkRoundtablesWithPoint(tag);
          this.selected_point_tag = tag;
        }
        if (this.roundtables_with_point.length === 0 || this.getProfile.is_criteria_editing_message_disabled === true) {
          this.show_point_creation_modal = true;
        }
      },
      closeDeletePointModal: function () {
        this.delete_point_index = -1;
        this.show_delete_point_modal = false;
      },
      deleteAssessmentPoint: function (tag) {
        this.delete_point_index =
          this.getCourseDetail.points_settings.peer_rewards.findIndex(
            (x) => x.tag == tag
          );
        this.checkRoundtablesWithPoint(tag);
        if (!this.roundtables_with_point.length || this.getProfile.is_criteria_editing_message_disabled === true) {
          this.show_delete_point_modal = true;
        }
      },
      confirmDeleteAssessmentPoint: async function () {
        let clonedCoursePointSettings =
          this.getCourseDetail.points_settings.peer_rewards.slice();
        clonedCoursePointSettings.splice(this.delete_point_index, 1);
        let payload = [];
        if (this.roundtables_with_point.length) {
          for (let i = 0; i < this.roundtables_with_point.length; i += 1) {
            const rt = this.getCourseDetail.roundtables.find(
              (r) => r.id === this.roundtables_with_point[i].id
            );
            const index_rt = rt.points_settings.peer_rewards.findIndex(
              (x) => x.tag === this.selected_point_tag
            );
            const peer_rewards = rt.points_settings.peer_rewards.slice();
            peer_rewards.splice(index_rt, 1);
            payload.push({ id: rt.id, peer_rewards });
          }
        }
        this.delete_point_index = -1;
        if (payload.length) {
          await store.dispatch('updateRTsCriteria', {
            id: this.getCourseDetail.id,
            criterias: payload,
            activityType: 'written',
          });
          const currentRTChanged = payload.find(
            (x) => x.id === this.getCurrentRT.id
          );
          if (currentRTChanged) {
            const { peer_rewards } = currentRTChanged;
            const cloned_rt = Object.assign({}, this.getCurrentRT);
            cloned_rt.points_settings = { peer_rewards };
            store.commit('UPDATE_RT_DETAILS', cloned_rt);
          }
        }
        store
          .dispatch('updateCourseDetail', {
            id: this.getCourseDetail.id,
            points_settings: { peer_rewards: clonedCoursePointSettings },
          })
          .then(() => {
            this.show_delete_point_modal = false;
          });
      },
      closeAssessmentChangingAlert: function () {
        this.show_alert_assessment_changing = false;
        if (this.delete_point_index !== -1) {
          this.show_delete_point_modal = true;
        } else {
          this.show_point_creation_modal = true;
        }
      },
      editPeerQuestion: function (index) {
        const { peer_assessment_questions } = this.getCurrentRT;
        this.peer_assessment_questions = JSON.parse(
          JSON.stringify(peer_assessment_questions)
        );
        this.question_to_edit_index = index;
        this.show_edit_question_modal = true;
      },
      saveQuestion: function () {
        const payload = {
          peer_assessment_questions: this.peer_assessment_questions,
          id: this.getCurrentRT.id,
        };
        store.dispatch('savingRTDetails', payload).then(() => {
          this.forceRerender();
          this.show_edit_question_modal = false;
        });
      },
      deletePeerQuestion: function (index) {
        this.delete_question_index = index;
        this.show_delete_question_modal = true;
      },
      confirmDeletePeerQuestion: function () {
        const { peer_assessment_questions } = this.getCurrentRT;
        const cloned_peer_questions = JSON.parse(
          JSON.stringify(peer_assessment_questions)
        );
        cloned_peer_questions.splice(this.delete_question_index, 1);
        store
          .dispatch('savingRTDetails', {
            peer_assessment_questions: cloned_peer_questions,
            id: this.getCurrentRT.id,
          })
          .then(() => this.forceRerender());
      },
      forceRerender() {
        this.componentKey += 1;
      },
      rtIntegrationAssignment(sync_assessment) {
        this.show_integrations_modal = sync_assessment;
      },
      goToTab: function (tab, query) {
        this.route_selected = { tab_to_go: tab };
        if (!this.hasTeacherPermissionsInCourse) {
          if (this.getCurrentRT.require_submission && !this.getMySubmission.is_started) {
            EventBus.$emit('createToast', { type: 'Error', message: 'Sorry! You need to make a submission to access the discussion', time: 5000 });
            return;
          }
        }
        if (this.hasTeacherPermissionsInCourse && this.is_button_save_setting_enable === false && this.tab === 'settings' && tab !== 'settings'){
          this.is_settings_confirm_modal_open = true;
          return;
        }
        if (!this.isEditedResponseSave && tab !== 'prompt') {
          this.selectedTabBeforeLeave = tab;
          this.show_leave_edit_submission = true;
          return;
        }
        this.tab = tab;
        const route_name = 'roundtable.' + tab;
        this.$router.push({ name: route_name, query: query !== null ? query : '' });
        return;
      },
      integration_rt_connected(integration) {
        if (
          this.getCurrentRT &&
          this.getCurrentRT.edlink_integrations &&
          this.getCurrentRT.edlink_integrations.length
        ) {
          const edlink_integration = this.getCurrentRT.edlink_integrations.find(
            (edl) => {
              if (edl.provider_name) {
                return (
                  edl.provider_name.toLowerCase() === integration.toLowerCase()
                );
              }
            }
          );
          if (edlink_integration) {
            return true;
          }
        }
        return false;
      },
      showRTWelcomeModalQuestionIcon() {
        getWelcomeSettings
          .getSettingsByQuery('online')
          .then((welcomeSettings) => {
            let settings;
            // If the user is a teacher.
            if (this.hasTeacherPermissionsInCourse) {
              settings = welcomeSettings.find((s) => s.role === 'teacher');
            } else {
              settings = welcomeSettings.find((s) => s.role === 'student');
            }
            if (settings.enabled) {
              this.showQuestionIcon = true;
            }
          });
      },
      saveSettingsTab: async function () {
        if (this.is_button_save_setting_enable !== true) {
          await this.$refs.childComponent.saveSettings();
          this.is_settings_confirm_modal_open = false;
          if (this.route_selected.tab_to_go) {
            this.is_button_save_setting_enable = true;
            this.goToTab(this.route_selected.tab_to_go);
          }
          
        }
      },
      handleFecthProviders: async function () {
        const { courseID } = this.$route.params;
        const providers = await this.$store.dispatch(
          'getSsoCourseProviders',
          courseID
        );
        return providers;
      },
      handleOpenStartFollowUP: async function (newFollowUpType) {
        const payload = {
          title: "",
          prompt: WrittenFollowUpTemplate,
          type: newFollowUpType,
          instructions: "Review the resources and the questions before the Verbal RoundTables starts. Take notes about answers that you have to the questions, and use your notepad to keep track of your ideas as the conversation unfolds.",
          user_id: this.getProfile.id,
          courses: [this.getCurrentRT.course_id],
          is_follow_up: true,
          follow_up_from_id: this.getCurrentRT.id,
          follow_up_from_type: this.getCurrentRT.type,
          is_generated: false
        };

        if (!this.getProfile.is_paid && this.getProfile.activity_passes === 0) {
          const toast = { type: 'Info', message: 'Sorry, you have no free RoundTables left! Delete an old RoundTable or upgrade your account!', time: 5000, upgrade: true };
          return EventBus.$emit('createToast', toast);
        }

        const draft = await Axios.post(`/api/drafts/draft`, payload);
        if (!draft) {
          throw new Error('Something happened starting roundtable creation! Please try again');
        }
        await this.$store.dispatch('useActivityPass');
        this.selectedRoundTableToFollow = { 
          id: this.getCurrentRT.id,
          draftId: draft.data.id,
          currentRoundtableType: this.getCurrentRT.type, 
          type: newFollowUpType, 
          course: this.getCourseDetail,
        };
        this.isFollowUpOpen = true;
      },
      handleOpenFollowUpQuestions: async function(newFollowUpType) {

        const handleGenerateFollowUpWrittenTemplate = (summary, questions) => {
          const questionsHTML = questions.map(question => `<li>${question.question}</li>`).join('');
          return `
            <div>
              <div id="discussion-summary">
                <h2>Summary</h2>
                <p>${summary}</p>
              </div>
              <div id="discussion-questions">
                <h2>Discussion Questions</h2>
                <p>Answer the following questions as a follow-up to our original RoundTable discussion</p>
                <ol id="questions">
                  ${questionsHTML}
                </ol>
              </div>
              <div id="discussion-instructions">
                <h2>Peer Feedback Instructions</h2>
                <p>After submitting your response, read at least two of your classmates’ responses and post a reply.</p>
                <ul>
                  <li>Did they change your perspective in any way?</li>
                  <li>Do you agree or disagree? Why or why not?</li>
                  <li>Can you add to their ideas?</li>
                </ul>
              </div>
            </div>
          `;
        };
        
        const title = { content: ""};

        function handleFormatFollowUpTitle(text) {
          const regex = /Follow Up \((\d+)\)/g;
          const matches = [...text.matchAll(regex)];
          if (matches.length <= 1) {
            return text;
          }
          
          let minNumber = Infinity;
          let matchToRemove = "";

          for (const match of matches) {
            const num = parseInt(match[1], 10);
            if (num < minNumber) {
              minNumber = num;
              matchToRemove = match[0];
            }
          }
          
          text = text.replace(matchToRemove, "").replace(/\s*-\s*-/, "-").trim();
          return text;
        }
      
        title.content = `${this.getCurrentRT.title} - Follow Up (1)`;

        if (this.getCurrentRT.sequence_id !== null) {
          title.content = handleFormatFollowUpTitle(`${this.getCurrentRT.title} - Follow Up (${this.getCurrentRT.roundtables_sequence.length})`);
        }

        const formatted_follow_up_questions = [];

        this.getCurrentRT.genie_recap.questions.map((q, index) => {
          formatted_follow_up_questions.push({id: index, question: q.question, is_active: true, is_custom: true });
        });

        const allPrompt = handleGenerateFollowUpWrittenTemplate(this.getCurrentRT.genie_recap.prompt_summary, this.getCurrentRT.genie_recap.questions);
        const payload = {
          title: title.content,
          prompt: allPrompt,
          type: newFollowUpType,
          instructions: this.getCurrentRT.genie_recap.prompt_summary || "Review the resources and the questions before the Verbal RoundTables starts. Take notes about answers that you have to the questions, and use your notepad to keep track of your ideas as the conversation unfolds.",
          user_id: this.getProfile.id,
          courses: [this.getCurrentRT.course_id],
          is_follow_up: true,
          follow_up_from_id: this.getCurrentRT.id,
          follow_up_from_type: this.getCurrentRT.type,
          is_generated: true,
          toggleable_guiding_questions: formatted_follow_up_questions

        };
        if (!this.getProfile.is_paid && this.getProfile.activity_passes === 0) {
          const toast = { type: 'Info', message: 'Sorry, you have no free RoundTables left! Delete an old RoundTable or upgrade your account!', time: 5000, upgrade: true };
          return EventBus.$emit('createToast', toast);
        }

        const draft = await Axios.post(`/api/drafts/draft`, payload);
        if (!draft) {
          throw new Error('Something happened starting roundtable creation! Please try again');
        }
        await this.$store.dispatch('useActivityPass');
        this.selectedRoundTableToFollow = { 
          id: this.getCurrentRT.id,
          draftId: draft.data.id,
          currentRoundtableType: this.getCurrentRT.type, 
          type: newFollowUpType, 
          course: this.getCourseDetail,
        };
        this.isFollowUpOpen = true;
      },
      showDraftEvaluations(value) {
        this.draft_evaluations = value;
        this.show_draft_evalutions = true;
      },
      goToSubmitResponsePage: async function () {
        if (this.hasTeacherPermissionsInCourse) {
          const { roundtableID } = this.$route.params;
          this.tab = 'prompt';
          this.$router.push({
            name: 'roundtable.prompt',
            query: { enable_editing: true },
          });
          await this.$store.dispatch('fetchRoundtable', roundtableID);
          this.$router.replace({
            name: this.$route.name,
            params: this.$route.params,
            query: { startSubmission: true },
          });
          return;
        }
        EventBus.$emit('handleTab', 'prompt');
        this.$router.replace({
          name: this.$route.name,
          params: this.$route.params,
        });
        return;
      },
      closeTemplateModal() {
        this.templates_modal = false;
        EventBus.$emit('assessment-focus');
      },
      startFollowUp(value) {
        this.is_new_roundtable_open = true;
        if (value === 'written') {
          this.outside_option = AcceptedOutsideOptions.recap_wrt;
        } else {
          this.outside_option = AcceptedOutsideOptions.recap_vrt;
        }
      },
      editFollowUpQuestions() {
        this.edit_follow_up_questions_modal = true;
      },
    },
    beforeRouteUpdate: async function (to, from, next) {
      this.open_assessment = false;
      if (to.query.follow_up === true) {
        this.handleOpenStartFollowUP();
      }
      if (
        this.hasTeacherPermissionsInCourse === false &&
        to.name === 'roundtable.discussion'
      ) {
        if (
          this.currentSubmission.by_pass_date !== null &&
          this.currentSubmission.start_date === null
        ) {
          const toast = {
            type: 'Info',
            message: 'You are an observer in this RoundTable.',
            time: 3500,
          };
          EventBus.$emit('createToast', toast);
        }
      }

      if (this.isDraftResponseSave === false) {
        await EventBus.$emit('force_draft_save', {
          route_to_go: null,
          show_toast: false,
          update_RT: true,
        });
        await EventBus.$emit('handle_stop_save_submission');
      }

      if (this.isEditedResponseSave === false) {
        this.route_selected = to;
        this.show_leave_edit_submission = true;
        next(false);
        return;
      }

      if (to.params.roundtableID !== from.params.roundtableID) {
        store.dispatch('fetchRoundtable', to.params.roundtableID);
        this.leaveRoundtableSocket(from.params.roundtableID);
        this.leaveCourseSocket(from.params.courseID);
        store.dispatch('toggleRevealNames', false);
        this.$store.commit('setIsReloadComponent');
      }
      next();
    },
    beforeRouteLeave: async function (to, from, next) {
      this.open_assessment = false;
      if (this.isDraftResponseSave === false) {
        await EventBus.$emit('force_draft_save', {
          route_to_go: to,
          show_toast: true,
          update_RT: false,
        });
        return;
      }
      if (this.isEditedResponseSave === false) {
        this.route_selected = to;
        this.show_leave_edit_submission = true;
        return;
      }
      const { courseID } = this.$route.params;
      this.leaveCourseSocket(courseID);
      this.leaveRoundtableSocket(from.params.roundtableID);
      next();
    },
    mounted: async function () {
      const { courseID, roundtableID } = this.$route.params;
      
      this.setupCourseSocket(courseID);
      this.setupRoundtableSocket(roundtableID);
      this.handleFecthProviders();
      this.$store.commit('setIsFromNotification', true);
      await store.dispatch('fetchCourseDetails', courseID);
      await store.dispatch('fetchRoundtable', roundtableID);
      /* await this.$store.commit('ENABLE_ANNONYMOUS', this.getCurrentRT.enable_annonymous); */
      await store.dispatch('getRecapByRoundtableId', roundtableID);

      const members_channel = await window.pusher.channels.channels[
        `presence-course-${this.$route.params.courseID}`
      ];
      if (members_channel) {
        this.members_course_socket = members_channel.members;
      }
      /* Teacher Flow */
      if (this.hasTeacherPermissionsInCourse) {
        if (this.$route.params.courseID !== this.$store.state.course_sample_id) {
          await store.dispatch('createTeacherSubmission', roundtableID);
          await store.dispatch('myRTIdentity', roundtableID);
        }
        if (this.$route.query.inviteModalOpen) {
          this.pre_selected_tab_in_share_modal = 'integrations';
          this.isInviteStudentModalOpen = true;
          this.$router.replace({ query: {} });
        } else if (this.isFromNotificationState) {
          this.goToTab('discussion', this.$route.query);
          if (this.$route.query.follow_up) {
            this.handleOpenStartFollowUP();
          }
        } else if (
          this.$route.query.enable_editing === true
        ) {
          this.goToTab('prompt');
        } else {
          this.goToTab('discussion');
        }
      }
      /* No-Teeacher Flow */
      if (!this.hasTeacherPermissionsInCourse) {
        await store.dispatch('myRTIdentity', roundtableID);
        if (this.getMySubmission.opened === false) {
          await store.dispatch('openOnlineRoundtable', {
            id: this.getMyRTUserDetails.submission_id,
            rtId: roundtableID,
          });
        }
        /* Require Submission */
        if (this.getCurrentRT.require_submission) {
          if (this.getMyRTUserDetails.submission_is_started === false) {
            /* Add Some Verification */
          } else if (this.getMyRTUserDetails.submission_is_started === true) {
            if (this.$route.query && this.isFromNotificationState) {
              /* Add Query Params From Notifications */
              this.goToTab('discussion', this.$route.query);
            } else if (this.$route.query.enable_editing) {
              this.goToTab('prompt');
            } else {
              /* Normal Redirect */
              this.goToTab('discussion', this.$route.query);
              this.$store.commit('setIsFromNotification', false);
            }
          }
        } else {
          await store.dispatch('myRTIdentity', roundtableID);
          await store.dispatch('byPassSubmission', {
            submission_id: this.getMyRTUserDetails.submission_id,
          });
          if (this.$route.query && this.isFromNotificationState) {
            this.goToTab('discussion', this.$route.query);
          } else if (this.getMySubmission.by_pass_date === null) {
            this.goToTab('prompt');
          } else {
            this.goToTab('discussion', this.$route.query);
          }
        }
      }

      this.showRTWelcomeModalQuestionIcon();
      this.listenToRoundtableEndings();

      this.handleUpdateSubmissionWithClassroomId();

      const role = this.getProfile.role;
      if (role === 'teacher' && this.getProfile.is_onboarded === false) {
        EventBus.$emit('open-welcome-modal');
      }

      this.isLoadingWritten = false;

      if (this.getProfile.genie_feedback_feature_flag === false && this.hasTeacherPermissionsInCourse) {
        setTimeout(() => {
          this.showGeniePopover = true;
        }, 3000);
        
      }
  
      EventBus.$on('openAssessmentModal', () => {
        this.show_integrations_modal = true;
      });

      EventBus.$on('handleRevealNames', (identityValue) => {
        this.$store.commit('ENABLE_ANNONYMOUS', identityValue);
      });

      EventBus.$on('genie_grader_confirm_modal', (value, genie_type) => {
        this.is_to_show_genie_grader_confirm = value;
        this.type_genie_assessment = genie_type;
      });

      EventBus.$on('genie_feedback_modal', (status_open, submission) => {
        this.is_genie_feedback_modal_open = status_open;
        this.submission_genie_feedback = submission;
      });

      this.$nextTick(() => {
        window.addEventListener('resize', this.onResize);
        const element = document.getElementById('rigthGrid');
        const width = element.offsetWidth;
        this.width_right_grid = width;

        if (this.windowWith < 1024) {
          this.menuOpen = false;
          this.$emit('showSideMenu', this.menuOpen);
        }
      });

      if (
        this.hasTeacherPermissionsInCourse &&
        this.getCurrentRT.is_start_follow_up_notification &&
        this.getCurrentRT.sequence_id === null
      ) {
        const toast_start_follow_up = {
          type: 'Info',
          message:
            'More than 80% of students have participated. Do you want to create a follow-up question to this RoundTable?',
          link: {
            course_id: this.getCourseDetail.id,
            roundtable_id: this.getCurrentRT.id,
            follow_up: true,
          },
        };
        EventBus.$emit('createToast', toast_start_follow_up);
      }

      EventBus.$on('selectedIntegrationSync', (sync_assessment) => {
        this.show_integrations_modal = false;
        this.selected_integration = 'google-classroom';
        this.edLink = false;
        this.logo = '/static/google_class_round.png';
        this.sync_assessment = sync_assessment;
        return;
      });
      EventBus.$on(
        'selectedIntegrationSyncEdLink',
        (sync_assessment, integration_name) => {
          this.show_integrations_modal = false;
          this.selected_integration = integration_name;
          this.edLink = true;
          this.logo = `/static/integrations/${
            integration_name.charAt(0).toUpperCase() + integration_name.slice(1)
          }.png`;
          this.sync_assessment = sync_assessment;
          return;
        }
      );
      EventBus.$on('draftResponseNotSaved', (stautsResponse) => {
        this.isDraftResponseSave = stautsResponse;
      });
      EventBus.$on('editPromptNotSaved', (publishStatus) => {
        this.isEditedResponseSave = publishStatus;
      });

      EventBus.$on('student_panel', async (is_open, submission_id) => {
        if (submission_id) {
          if (
            this.hasTeacherPermissionsInCourse ||
            submission_id === this.getMyRTUserDetails.submission_id
          ) {
            await this.getSubmissionSummary(submission_id);
            this.open_assessment = true;
          }
          return;
        }
        this.open_assessment = is_open;
      });

      EventBus.$on('feed-is-mounted', () => {
        this.handleShowOverlayRouteQuery();
        setTimeout(() => {
          this.setUpSubmissionAuthorHighlighting();
        });
      });

      EventBus.$on('showRountableWelcomeModal', () => {
        this.showFirstRTWelcomeModal();
      });

      EventBus.$on('goToStudentSubmissionPage', () => {
        this.tab = 'prompt';
        this.$router.push({
          name: 'roundtable.prompt',
          query: { enable_editing: true },
        });
      });

      EventBus.$on('showRoundtableSummary', (scrollTo) => {
        this.$store.dispatch('userEnteredSummaryOverlay', false);
        this.scrollToInOverlay = scrollTo;
        this.show_overlay = 'summary';
      });

      EventBus.$on('open-invite-google-classroom-modal', () => {
        this.open_google_classroom_dialog = true;
      });

      EventBus.$on('submissionToSave', (value) => {
        this.user_has_changes_to_save = value;
      });

      EventBus.$on('integrationConnected', (integration) => {
        this.integration = integration;
        this.is_integration_connected = true;
      });

      EventBus.$on('integrationDisconnected', () => {
        this.is_integration_connected = false;
        this.fetchRoundtable(roundtableID);
      });

      EventBus.$on('seeContext', (submissionId, recapId, query) => {
        this.$router.replace({ query: query });
        if (submissionId) EventBus.$emit('scrollToSubmission', submissionId);
        else if (recapId) EventBus.$emit('scrollToRecap', recapId, query);
      });

      EventBus.$on('seeSubmissionStudentsTable', async (tab, submissionID) => {
        this.goToTab(tab);
        setTimeout(() => {
          this.selectSubmission(submissionID);
          EventBus.$emit('scrollToSubmission', submissionID);
        }, 1000);
      });

      EventBus.$on('handleTab', (tab, edit_prompt) => {
        this.goToTab(tab);
        if (edit_prompt) {
          this.$router.push({
            name: 'roundtable.prompt',
            params: { roundtableID: this.getCurrentRT.id },
            query: { enable_editing: true },
          });
        }
      });

      EventBus.$on('seeFeedback', (tab, submissionID) => {
        this.goToTab(tab);
        this.$router.replace({
          name: this.$route.name,
          params: this.$route.params,
          query: { submissionID: submissionID, assessment: true },
        });
      });
      EventBus.$on('showTemplateModal', () => {
        this.templates_modal = true;
      });

      if (role === 'teacher' && this.getProfile.is_onboarded === false) {
        EventBus.$emit('open-welcome-modal');
      }

      EventBus.$on('openInviteStudentsModal', () => {
        this.isInviteStudentModalOpen = true;
      });

      EventBus.$on('generate-genie-recap', () => {
        this.genie_recap = true;
      });
      EventBus.$on('sidebar-recap', (status) => {
        this.genie_recap = status;
      });

      EventBus.$on('handleOpenInstructionsModal', (value) => {
        this.isNewInstructionOpen = value;
      });

      EventBus.$on('handleOpenGenieGraderModal', () => {
        this.is_to_show_genie_grader_confirm = true;
      });
    },
    beforeDestroy() {
      EventBus.$emit('close_toast');
      document.removeEventListener('click', this.close);
      EventBus.$off('student-has-submitted');
      EventBus.$off('feed-is-mounted');
      EventBus.$off('showRountableWelcomeModal');
      EventBus.$off('submissionToSave');
      EventBus.$off('open-invite-google-classroom-modal');
      EventBus.$off('integrationConnected');
      EventBus.$off('integrationDisconnected');
      EventBus.$off('seeContext');
      EventBus.$off('seeSubmissionStudentsTable');
      EventBus.$off('handleTab');
      EventBus.$off('draftResponseNotSaved');
      EventBus.$off('editPromptNotSaved');
      EventBus.$off('openAssessmentModal');
      EventBus.$off('seeFeedback');
      EventBus.$off('showTemplateModal');
      EventBus.$off('selectedIntegrationSync');
      EventBus.$off('selectedIntegrationSyncEdLink');
      EventBus.$off('goToStudentSubmissionPage');
      EventBus.$off('openInviteStudentsModal');
      EventBus.$off('genie_grader_confirm_modal');
      EventBus.$off('open_generating_assessment_individual');
      EventBus.$off('generate-genie-recap');
      EventBus.$off('sidebar-recap');
      EventBus.$off('genie_feedback_modal');
      EventBus.$off('handleOpenInstructionsModal');
      EventBus.$off('handleOpenGenieGraderModal');
      $('#feed-body').off('resize scroll');
      clearInterval(this.checkRTsEndedCron);
      this.leaveRoundtableSocket(this.getCurrentRT.id);

      //this.$store.dispatch('userEnteredSummaryOverlay', false);
      window.removeEventListener('resize', this.onResize);
    },
  });
</script>

<style scoped>
  .rt-body {
    min-height: calc(100vh - 160px);
  }
  .tab-body {
    min-height: calc(100vh - 246px);
  }
  .submission-response-body {
    min-height: calc(100vh - 144px);
  }
  .prompt-heigth {
    height: calc(100vh - 160px);
  }
  .max-w-screen-3xl {
    max-width: 1920px;
  }
</style>
<style lang="scss" scoped>
  input[type='checkbox'] {
    width: 1.1rem !important;
    height: 1.1rem !important;
    -webkit-appearance: none;
    -moz-appearance: none;
    -o-appearance: none;
    appearance: none;
    box-shadow: none;
    font-size: 0.8rem;
    text-align: center;
    line-height: 1rem;
    border-radius: 0.25rem;
    border: solid 1px #cfcfcf;
    --tw-bg-opacity: 1;
    background-color: rgba(243, 244, 246, var(--tw-bg-opacity));
  }
  input[type='checkbox']:checked {
    background-color: #72ccd7;
    border: solid 1px #72ccd7;
  }
  input[type='checkbox']:checked::after {
    font-family: 'Font Awesome 5 Free';
    content: '\f00c';
    font-weight: 900;
    color: white;
    line-height: 1rem;
  }
</style>



// WEBPACK FOOTER //
// rtIndex.vue?6a0cf88a